/*
 * Copyright 2026 Hypergiant Galactic Systems Inc. All rights reserved.
 * This file is licensed to you under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License. You may obtain a copy
 * of the License at https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software distributed under
 * the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR REPRESENTATIONS
 * OF ANY KIND, either express or implied. See the License for the specific language
 * governing permissions and limitations under the License.
 */
'use client';

import 'client-only';
import { clsx } from '@accelint/design-foundation/lib/utils';
import { composeRenderProps } from 'react-aria-components/composeRenderProps';
import { useContextProps } from 'react-aria-components/slots';
import { Link } from 'react-aria-components/Link';
import { IconProvider } from '../icon/context';
import { LinkButtonContext } from './context';
import styles from './styles.module.css';
import type { LinkButtonProps } from './types';

/**
 * LinkButton - A button component that renders as a link element
 *
 * Provides accessible link functionality with button-like styling and visual feedback.
 * Perfect for navigation actions that should look like buttons but behave as links.
 * Includes icon support and integrates with React Aria for keyboard navigation and accessibility.
 *
 * @param props - The link button props.
 * @param props.ref - Reference to the anchor element.
 * @param props.children - Link button content (text, icons, or both).
 * @param props.className - Additional CSS class names for styling.
 * @param props.color - Semantic color variant.
 * @param props.size - Size of the link button.
 * @param props.variant - Visual style variant.
 * @param props.href - Navigation destination URL.
 * @returns The link button component.
 *
 * @example
 * ```tsx
 * // Basic link button
 * <LinkButton href="/dashboard">Go to Dashboard</LinkButton>
 * ```
 *
 * @example
 * ```tsx
 * // Link button with different variants and sizes
 * <LinkButton variant="filled" size="large" href="/create">Create New</LinkButton>
 * <LinkButton variant="outline" size="small" href="/settings">Settings</LinkButton>
 * ```
 *
 * @example
 * ```tsx
 * // Link button with icon
 * <LinkButton variant="flat" href="/profile">
 *   <Icon><User /></Icon>
 *   View Profile
 * </LinkButton>
 * ```
 *
 * @example
 * ```tsx
 * // Icon-only link button
 * <LinkButton variant="icon" href="/help">
 *   <Icon><HelpCircle /></Icon>
 * </LinkButton>
 * ```
 *
 * @example
 * ```tsx
 * // Link button with different colors
 * <LinkButton color="critical" href="/delete">Delete Account</LinkButton>
 * <LinkButton color="serious" href="/reset">Reset Data</LinkButton>
 * ```
 */
export function LinkButton({ ref, ...props }: LinkButtonProps) {
  [props, ref] = useContextProps(props, ref ?? null, LinkButtonContext);

  const {
    children,
    className,
    color = 'mono-muted',
    size = 'medium',
    variant = 'filled',
    ...rest
  } = props;

  return (
    <IconProvider size={size}>
      <Link
        {...rest}
        ref={ref}
        className={composeRenderProps(className, (className) =>
          clsx('group/button', styles.button, styles[variant], className),
        )}
        data-color={color}
        data-size={size}
      >
        {children}
      </Link>
    </IconProvider>
  );
}
